import React, { useEffect } from 'react';
import { motion } from 'framer-motion';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastProps {
  message: string;
  type: ToastType;
  onClose: () => void;
  duration?: number;
}

const Toast: React.FC<ToastProps> = ({ message, type, onClose, duration = 5000 }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return '✓';
      case 'error':
        return '✕';
      case 'warning':
        return '⚠';
      case 'info':
        return 'ℹ';
      default:
        return 'ℹ';
    }
  };

  const getColors = () => {
    switch (type) {
      case 'success':
        return 'bg-green-500 border-green-600';
      case 'error':
        return 'bg-red-500 border-red-600';
      case 'warning':
        return 'bg-yellow-500 border-yellow-600';
      case 'info':
        return 'bg-blue-500 border-blue-600';
      default:
        return 'bg-gray-500 border-gray-600';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -8, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.96 }}
      className={`${getColors()} pointer-events-auto w-full max-w-full min-w-0 rounded-xl border-2 px-4 py-3.5 text-white shadow-2xl flex items-start gap-3 sm:w-auto sm:min-w-[300px] sm:max-w-[500px] sm:items-center sm:px-6 sm:py-4`}
    >
      <div className="flex-shrink-0 text-2xl font-bold leading-none">{getIcon()}</div>
      <div className="min-w-0 flex-1 break-words text-sm font-medium leading-snug">{message}</div>
      <button
        onClick={onClose}
        className="flex-shrink-0 text-xl font-bold leading-none text-white transition-colors hover:text-gray-200"
      >
        ×
      </button>
    </motion.div>
  );
};

export default Toast;
